import { Address } from '@synergy/models';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage } from 'react-intl';
import { FormikPoolDatePicker } from '@synergy/components';
import { PaloDetailSlot, PaloField, PaloFormGrid, PaloSegmentedToggle } from '@synergy/components-palo';
import { connect } from 'react-redux';

import { AppState } from '../../../store/models/app-state';
import { Contact } from '../../../common/models/contact';
import { Customer } from '../../../common/models/customer';
import { PaloCustomerQuickSearch } from '../../../common/components/palo/palo-customer-quick-search';
import { PaloSalesOrderCustomerLock } from '../../../common/components/palo/palo-locked-badge';
import {
    PartialAccountContact,
    PoolAccountContactAutocompleteList,
} from '../../../common/components/autocomplete-list/account-contact-autocomplete-list/pool-account-contact-autocomplete-list';
import {
    PartialContact,
    PoolContactAutocompleteList,
} from '../../../common/components/autocomplete-list/contact-autocomplete-list/pool-contact-autocomplete-list';
import { PoolCustomerAutocompleteList } from '../../../common/components/autocomplete-list/customer-autocomplete-list/pool-customer-autocomplete-list';
import { SalesOrderDetailFormValues } from '../models/sales-order-detail-form-values';

interface SalesOrderDetailCommonOwnProps {
    readonly?: boolean;
    onCustomerSelected?: (customer: Customer | null, contact?: Contact, address?: Address) => void;
}

interface SalesOrderDetailCommonReduxProps {
    contacts: Contact[];
}

type PaloSalesOrderDetailCommonProps = SalesOrderDetailCommonOwnProps & SalesOrderDetailCommonReduxProps;

/**
 * The head of an order in one line: the customer, with the quick search in
 * its field, the contact and the handler. The deadline for an inquiry made
 * from the order joins them only when it becomes one (#2259).
 */
class PaloSalesOrderDetailCommonBase extends FormComponent<
    SalesOrderDetailFormValues,
    PaloSalesOrderDetailCommonProps
> {
    private handleCustomerSelected =
        (form: FormikProps<SalesOrderDetailFormValues>) => (selectedCustomer: Customer | null) => {
            const currentId = form.values.customerAccount && form.values.customerAccount.id;
            const selectedId = selectedCustomer && selectedCustomer.id;
            if (currentId === selectedId) {
                return;
            }
            this.props.onCustomerSelected?.(selectedCustomer);
        };

    private getCurrentAccountId(formikBag: FormikProps<SalesOrderDetailFormValues>) {
        return formikBag.values.customerAccount?.id;
    }

    public render() {
        const { readonly, contacts } = this.props;

        return (
            <Field name="generatesPurchaseOrder">
                {({ field: kind, form: formikBag }: FieldProps<boolean>) => (
                    <>
                        {/* The kind of order stands where the title is, as the
                            kind of an invoice or an inquiry does (#2250). */}
                        <PaloDetailSlot name="kind">
                            <PaloSegmentedToggle
                                value={kind.value ?? false}
                                onChange={this.setFieldValue(formikBag, kind.name)}
                                disabled={readonly}
                                options={[
                                    {
                                        value: false,
                                        label: (
                                            <FormattedMessage id="app.salesOrders.detail.generatesPurchaseOrder.false" />
                                        ),
                                    },
                                    {
                                        value: true,
                                        label: (
                                            <FormattedMessage id="app.salesOrders.detail.generatesPurchaseOrder.true" />
                                        ),
                                    },
                                ]}
                            />
                        </PaloDetailSlot>
                        <PaloFormGrid columns={4} labels="side">
                            <Field name="customerAccount">
                                {({ field, form }: FieldProps<Customer>) => (
                                    <PaloField
                                        label={
                                            <>
                                                <FormattedMessage id="app.label.customer" /> *
                                            </>
                                        }
                                        name={field.name}
                                        span={2}
                                        badge={<PaloSalesOrderCustomerLock />}
                                        error={
                                            this.hasError(form, field.name)
                                                ? this.getError(form, field.name)
                                                : undefined
                                        }
                                    >
                                        <PaloCustomerQuickSearch
                                            id="sales-order-quick-search"
                                            picked={!!form.values.customerAccount}
                                            readonly={readonly}
                                            onSelected={(customer, contact, address) =>
                                                this.props.onCustomerSelected?.(customer, contact, address)
                                            }
                                        >
                                            {searchButton => (
                                                <PoolCustomerAutocompleteList
                                                    id="quick-search-customers"
                                                    label={<></>}
                                                    onSelected={this.handleCustomerSelected(form)}
                                                    initialValue={''}
                                                    error={this.hasError(form, field.name)}
                                                    readOnly={readonly}
                                                    {...field}
                                                    adornment={searchButton}
                                                    isLocked={false}
                                                />
                                            )}
                                        </PaloCustomerQuickSearch>
                                    </PaloField>
                                )}
                            </Field>
                            <Field name="customerContact">
                                {({ field, form }: FieldProps<PartialAccountContact>) => (
                                    <PaloField
                                        label={
                                            <>
                                                <FormattedMessage id="app.label.customerContact" /> *
                                            </>
                                        }
                                        name={field.name}
                                        error={
                                            this.hasError(form, field.name)
                                                ? this.getError(form, field.name)
                                                : undefined
                                        }
                                    >
                                        <PoolAccountContactAutocompleteList
                                            accountId={this.getCurrentAccountId(form)}
                                            label={<></>}
                                            disabled={!form.values.customerAccount}
                                            onSelected={this.setFieldValue(form, field.name)}
                                            error={this.hasError(form, field.name)}
                                            readOnly={readonly}
                                            {...field}
                                        />
                                    </PaloField>
                                )}
                            </Field>
                            <Field name="responsibleContact">
                                {({ field, form }: FieldProps<PartialContact>) => (
                                    <PaloField
                                        label={
                                            <>
                                                <FormattedMessage id="app.common.responsibleContact" /> *
                                            </>
                                        }
                                        name={field.name}
                                        span={2}
                                        error={
                                            this.hasError(form, field.name)
                                                ? this.getError(form, field.name)
                                                : undefined
                                        }
                                    >
                                        <PoolContactAutocompleteList
                                            items={contacts}
                                            label={<></>}
                                            onSelected={this.setFieldValue(form, field.name)}
                                            required={false}
                                            error={this.hasError(form, field.name)}
                                            readOnly={readonly}
                                            {...field}
                                        />
                                    </PaloField>
                                )}
                            </Field>
                            {!kind.value && (
                                <Field name="dueDate">
                                    {({ field, form }: FieldProps<Date>) => (
                                        <PaloField
                                            /* Not the date of the order: it becomes the
                                               deadline of the inquiry made from it, and
                                               empty means two working days (#829). */
                                            label={<FormattedMessage id="app.label.salesOrder.dueDate" />}
                                            name={field.name}
                                            error={
                                                this.hasError(form, field.name)
                                                    ? this.getError(form, field.name)
                                                    : undefined
                                            }
                                        >
                                            <FormikPoolDatePicker
                                                name="dueDate"
                                                readOnly={readonly}
                                                disablePast={true}
                                            />
                                        </PaloField>
                                    )}
                                </Field>
                            )}
                        </PaloFormGrid>
                    </>
                )}
            </Field>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    contacts: state.contacts.sortedByName,
});

export const PaloSalesOrderDetailCommon = connect(mapStateToProps, {})(PaloSalesOrderDetailCommonBase);
